import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {Button, Dialog, DialogBody, Tag, Spinner, Icon} from '@blueprintjs/core';
import styled from 'styled-components';
import {device} from '/src/services/iot-api';

const POLL_INTERVAL = 10000;
const MAX_POLL_INTERVAL = 60000;
const BACKOFF_MULTIPLIER = 1.5;
const MAX_POINTS = 30;

// -- Styled components --

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1em;
    font-size: 14px;
    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
`;

const SystemSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75em;
`;

const MetricCard = styled.div`
    background: rgba(0, 0, 0, 0.05);
    border-radius: 6px;
    padding: 0.6em 0.8em;
`;

const MetricHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.3em;
`;

const MetricTitle = styled.span`
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MetricDetails = styled.div`
    display: flex;
    gap: 0.75em;
    margin-top: 0.3em;
    font-size: 12px;
    color: #5c7080;
`;

const SectionDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #5c7080;
    &::before {
        content: '';
        flex: 1;
        border-bottom: 1px solid rgba(0,0,0,0.1);
        order: 1;
    }
    & > *:last-child { order: 2; }
`;

const PipelineGroup = styled.div`
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
    overflow: hidden;
`;

const PipelineHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.4em 0.6em;
    cursor: pointer;
    user-select: none;
    font-weight: 600;
    font-size: 13px;
    background: rgba(0, 0, 0, 0.04);
    &:hover { background: rgba(0, 0, 0, 0.08); }
`;

const NodeTable = styled.div`
    font-size: 12px;
`;

const NodeRow = styled.div`
    display: grid;
    grid-template-columns: minmax(100px, 1.2fr) 60px 55px 55px 50px 35px 60px;
    gap: 2px;
    padding: 2px 0.6em;
    align-items: center;
    &:nth-child(even) { background: rgba(0, 0, 0, 0.02); }
`;

const NodeHeaderRow = styled(NodeRow)`
    font-weight: 600;
    color: #5c7080;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-size: 10px;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    padding-top: 4px;
    padding-bottom: 4px;
`;

const NodeName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const NumCell = styled.span`
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const Bar = styled.div`
    height: 10px;
    border-radius: 2px;
    background: rgba(0,0,0,0.06);
    overflow: hidden;
    position: relative;
`;

const BarFill = styled.div`
    height: 100%;
    border-radius: 2px;
    background: ${p => p.$color || '#2965cc'};
    width: ${p => Math.min(p.$pct, 100)}%;
    transition: width 0.3s ease;
`;

const StatsScroll = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5em;
`;

// -- Sparkline --

const Sparkline = ({data, color, max = 100, height = 32, width = 200}) => {
    if (!data.length) return null;
    const pad = 2;
    const w = width - pad * 2;
    const h = height - pad * 2;
    const pts = data.map((val, i) => {
        const x = pad + (data.length === 1 ? w : (i / (MAX_POINTS - 1)) * w);
        const y = pad + h - (Math.min(val, max) / max) * h;
        return `${x},${y}`;
    });
    const area = [`${pts[0].split(',')[0]},${height - pad}`, ...pts, `${pts[pts.length - 1].split(',')[0]},${height - pad}`].join(' ');
    const last = pts[pts.length - 1].split(',');
    return (
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
            <polygon points={area} fill={color} opacity="0.15" />
            <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={last[0]} cy={last[1]} r="2" fill={color} />
        </svg>
    );
};

// -- Helpers --

const _formatBytes = (bytes) => {
    if (bytes == null) return '?';
    const gb = bytes / (1024 ** 3);
    if (gb >= 1) return `${gb.toFixed(1)}G`;
    const mb = bytes / (1024 ** 2);
    return `${mb.toFixed(0)}M`;
};

const _intentForPercent = (pct) => {
    if (pct == null) return 'none';
    if (pct >= 90) return 'danger';
    if (pct >= 70) return 'warning';
    return 'success';
};

const _formatMs = (seconds) => {
    if (seconds == null || seconds === 'NA') return '-';
    const ms = typeof seconds === 'string' ? parseFloat(seconds) : seconds * 1000;
    if (isNaN(ms)) return '-';
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms >= 1) return `${ms.toFixed(0)}ms`;
    return `${(ms * 1000).toFixed(0)}us`;
};

const _formatCount = (n) => {
    if (n == null) return '-';
    const num = typeof n === 'string' ? parseInt(n, 10) : n;
    if (isNaN(num)) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
};

const _delayColor = (ms) => {
    if (ms == null || isNaN(ms)) return '#ced9e0';
    if (ms > 20000) return '#c23030';
    if (ms > 10000) return '#d99e0b';
    return '#29a634';
};

const _stateTag = (state) => {
    if (!state) return null;
    const s = state.toUpperCase();
    const intent = s === 'RUNNING' ? 'success' : s === 'STOPPED' ? 'none' : s === 'CRASHED' ? 'danger' : 'warning';
    return <Tag minimal round intent={intent} style={{fontSize: 11, minHeight: 16, padding: '0 5px', lineHeight: '16px'}}>{s}</Tag>;
};

/**
 * Normalise both APU (flat) and NUC (tree) formats into a common structure:
 * [ { pipeline, state, nodes: [{ name, state, calls, avg, last, max, prods, drops, delay, queue_in }] } ]
 *
 * APU format: { stats: [{ pipeline, name: "NodeType<Inner>", calls: "123", avg: "89" (us), ... }] }
 * NUC format: { type: "Pipeline", children: [{ type: "Node"|"Pipeline", ... }] }
 */
const _normaliseStats = (raw) => {
    if (!raw) return [];

    // APU format — flat list
    if (raw.stats && Array.isArray(raw.stats)) {
        const groups = {};
        for (const row of raw.stats) {
            const pName = row.pipeline || '_main';
            if (!groups[pName]) groups[pName] = {pipeline: pName, state: 'RUNNING', nodes: []};
            // APU times are in microseconds (strings)
            const toMs = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n / 1000; };
            const toNum = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
            const queueIn = row.queue_in === 'NA' ? null : toNum(row.queue_in);
            const delay = row.delay === 'NA' ? null : toMs(row.delay);
            groups[pName].nodes.push({
                name: row.name?.replace(/^[A-Za-z]+Node<(.+)>$/, '$1') || row.name || '?',
                fullName: row.name || '?',
                state: row.state || 'RUNNING',
                calls: toNum(row.calls),
                avgMs: toMs(row.avg),
                lastMs: toMs(row.last),
                maxMs: toMs(row.max),
                prods: toNum(row.prods),
                drops: toNum(row.drops),
                delayMs: delay,
                queueIn: queueIn,
            });
        }
        return Object.values(groups);
    }

    // NUC format — hierarchical tree
    if (raw.type === 'Pipeline' && raw.children) {
        const result = [];
        const mainNodes = [];

        for (const child of raw.children) {
            if (child.type === 'Pipeline') {
                const nodes = (child.children || [])
                    .filter(n => n.type === 'Node' && n.node_mode !== 'initialiser')
                    .map(n => ({
                        name: n.name || '?',
                        fullName: n.name || '?',
                        state: n.state || '?',
                        calls: n.calls ?? null,
                        avgMs: n.avg != null ? n.avg * 1000 : null,
                        lastMs: n.last != null ? n.last * 1000 : null,
                        maxMs: n.max != null ? n.max * 1000 : null,
                        prods: n.prods ?? null,
                        drops: n.drops ?? null,
                        delayMs: n.delay != null ? n.delay * 1000 : null,
                        queueIn: n.queue_in ?? null,
                    }));
                if (nodes.length)
                    result.push({pipeline: child.name || '?', state: child.state || '?', nodes});
            } else if (child.type === 'Node' && child.node_mode !== 'initialiser') {
                mainNodes.push({
                    name: child.name || '?',
                    fullName: child.name || '?',
                    state: child.state || '?',
                    calls: child.calls ?? null,
                    avgMs: child.avg != null ? child.avg * 1000 : null,
                    lastMs: child.last != null ? child.last * 1000 : null,
                    maxMs: child.max != null ? child.max * 1000 : null,
                    prods: child.prods ?? null,
                    drops: child.drops ?? null,
                    delayMs: child.delay != null ? child.delay * 1000 : null,
                    queueIn: child.queue_in ?? null,
                });
            }
        }

        if (mainNodes.length)
            result.unshift({pipeline: raw.name || 'Main', state: raw.state || '?', nodes: mainNodes});

        return result;
    }

    return [];
};


// -- Pipeline stats section --

const PipelineStats = ({pipelines}) => {
    const [collapsed, setCollapsed] = useState({});

    const toggle = useCallback((name) => {
        setCollapsed(prev => ({...prev, [name]: !prev[name]}));
    }, []);

    if (!pipelines.length)
        return <Tag minimal intent="warning">No pipeline data</Tag>;

    return (
        <StatsScroll>
            {pipelines.map((p, i) => {
                const isOpen = !collapsed[p.pipeline];
                const maxDelay = Math.max(...p.nodes.map(n => n.delayMs ?? 0));
                const totalDrops = p.nodes.reduce((sum, n) => sum + (n.drops ?? 0), 0);
                return (
                    <PipelineGroup key={p.pipeline + i}>
                        <PipelineHeader onClick={() => toggle(p.pipeline)}>
                            <Icon icon={isOpen ? 'chevron-down' : 'chevron-right'} size={12} />
                            <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.pipeline}</span>
                            {_stateTag(p.state)}
                            {maxDelay > 0 && (
                                <Tag minimal round style={{fontSize: 11, minHeight: 16, padding: '0 5px', color: _delayColor(maxDelay)}}>
                                    {_formatMs(maxDelay / 1000)}
                                </Tag>
                            )}
                            {totalDrops > 0 && (
                                <Tag minimal round intent="danger" style={{fontSize: 11, minHeight: 16, padding: '0 5px'}}>
                                    {_formatCount(totalDrops)} drops
                                </Tag>
                            )}
                            <span style={{color: '#8a9ba8', fontSize: 11}}>{p.nodes.length}n</span>
                        </PipelineHeader>
                        {isOpen && (
                            <NodeTable>
                                <NodeHeaderRow>
                                    <span>Node</span>
                                    <NumCell>Calls</NumCell>
                                    <NumCell>Avg</NumCell>
                                    <NumCell>Last</NumCell>
                                    <NumCell>Drops</NumCell>
                                    <NumCell>Q</NumCell>
                                    <span>Delay</span>
                                </NodeHeaderRow>
                                {p.nodes.map((n, j) => (
                                    <NodeRow key={n.name + j}>
                                        <NodeName title={n.fullName}>
                                            {n.state === 'CRASHED' && <Icon icon="error" size={10} intent="danger" style={{marginRight: 3}} />}
                                            {n.name}
                                        </NodeName>
                                        <NumCell>{_formatCount(n.calls)}</NumCell>
                                        <NumCell>{_formatMs(n.avgMs / 1000)}</NumCell>
                                        <NumCell>{_formatMs(n.lastMs / 1000)}</NumCell>
                                        <NumCell style={n.drops > 0 ? {color: '#c23030', fontWeight: 600} : undefined}>
                                            {n.drops != null ? _formatCount(n.drops) : '-'}
                                        </NumCell>
                                        <NumCell>{n.queueIn != null ? n.queueIn : '-'}</NumCell>
                                        <Bar title={n.delayMs != null ? `${n.delayMs.toFixed(0)}ms` : ''}>
                                            <BarFill
                                                $color={_delayColor(n.delayMs)}
                                                $pct={n.delayMs != null ? Math.min((n.delayMs / 30000) * 100, 100) : 0}
                                            />
                                        </Bar>
                                    </NodeRow>
                                ))}
                            </NodeTable>
                        )}
                    </PipelineGroup>
                );
            })}
        </StatsScroll>
    );
};


// -- Main component --

const CountdownTimer = ({loading, seconds}) => {
    if (seconds == null) return <span />;
    if (loading) return <Spinner size={11} />;
    return (
        <span style={{fontSize: 11, color: '#8a9ba8', fontVariantNumeric: 'tabular-nums'}}>
            {seconds}s
        </span>
    );
};

const _startCountdown = (setCountdown, countdownRef, seconds) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(seconds);
    countdownRef.current = setInterval(() => setCountdown(prev => Math.max(prev - 1, 0)), 1000);
};

const DeviceTopOverlay = ({deviceName, abortController}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pipelinesExpanded, setPipelinesExpanded] = useState(false);
    const [history, setHistory] = useState({cpu: [], ram: [], swap: [], disk: []});
    const [latest, setLatest] = useState(null);
    const [pipelines, setPipelines] = useState([]);
    const [unresponsive, setUnresponsive] = useState(false);
    const [error, setError] = useState(null);
    const [psFetching, setPsFetching] = useState(false);
    const [statsFetching, setStatsFetching] = useState(false);
    const [psCountdown, setPsCountdown] = useState(POLL_INTERVAL / 1000);
    const [statsCountdown, setStatsCountdown] = useState(POLL_INTERVAL / 1000);
    const psTimeoutRef = useRef(null);
    const psCountdownRef = useRef(null);
    const statsTimeoutRef = useRef(null);
    const statsCountdownRef = useRef(null);
    const hiddenSinceRef = useRef(null);
    const psDelayRef = useRef(POLL_INTERVAL);
    const statsDelayRef = useRef(POLL_INTERVAL);

    const schedulePsPoll = useCallback((delay) => {
        if (psTimeoutRef.current) clearTimeout(psTimeoutRef.current);
        psTimeoutRef.current = setTimeout(() => {
            if (hiddenSinceRef.current && Date.now() - hiddenSinceRef.current > 5 * 60 * 1000) return;
            fetchPsInfo();
        }, delay);
    }, []);

    const scheduleStatsPoll = useCallback((delay) => {
        if (statsTimeoutRef.current) clearTimeout(statsTimeoutRef.current);
        statsTimeoutRef.current = setTimeout(() => fetchStats(), delay);
    }, []);

    const fetchPsInfo = useCallback(() => {
        setPsFetching(true);
        const signal = abortController.signal;

        device.command('GetPsInfoCommand', deviceName, null, 'command', {signal})
            .then(resp => {
                const isNack = resp.topic?.split('/').pop() === 'nack';
                const data = resp.payload?.resp;
                if (isNack || !data?.cpu) {
                    setPsFetching(false);
                    const nextDelay = Math.min(psDelayRef.current * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL);
                    psDelayRef.current = nextDelay;
                    _startCountdown(setPsCountdown, psCountdownRef, Math.round(nextDelay / 1000));
                    schedulePsPoll(nextDelay);
                    setUnresponsive(true);
                    return;
                }
                setPsFetching(false);
                psDelayRef.current = POLL_INTERVAL;
                _startCountdown(setPsCountdown, psCountdownRef, POLL_INTERVAL / 1000);
                schedulePsPoll(POLL_INTERVAL);
                setUnresponsive(false);
                setError(null);
                setLatest(data);
                setHistory(prev => ({
                    cpu: [...prev.cpu, data.cpu?.cpu_percent ?? 0].slice(-MAX_POINTS),
                    ram: [...prev.ram, data.ram?.percent ?? 0].slice(-MAX_POINTS),
                    swap: [...prev.swap, data.swap?.percent ?? 0].slice(-MAX_POINTS),
                    disk: [...prev.disk, data.disk?.percent ?? 0].slice(-MAX_POINTS),
                }));
            })
            .catch(err => {
                if (err?.name === 'AbortError') return;
                setPsFetching(false);
                const nextDelay = Math.min(psDelayRef.current * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL);
                psDelayRef.current = nextDelay;
                _startCountdown(setPsCountdown, psCountdownRef, Math.round(nextDelay / 1000));
                schedulePsPoll(nextDelay);
                setError('Failed to fetch system info');
                setUnresponsive(true);
            });
    }, [deviceName]);

    const fetchStats = useCallback(() => {
        setStatsFetching(true);
        const signal = abortController.signal;

        device.command('GetNodeStatsSnapshotCommand', deviceName, null, 'command', {signal})
            .then(resp => {
                const isNack = resp.topic?.split('/').pop() === 'nack';
                const raw = resp.payload?.resp;
                if (isNack || !raw) {
                    setStatsFetching(false);
                    const nextDelay = Math.min(statsDelayRef.current * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL);
                    statsDelayRef.current = nextDelay;
                    _startCountdown(setStatsCountdown, statsCountdownRef, Math.round(nextDelay / 1000));
                    scheduleStatsPoll(nextDelay);
                    return;
                }
                setStatsFetching(false);
                statsDelayRef.current = POLL_INTERVAL;
                _startCountdown(setStatsCountdown, statsCountdownRef, POLL_INTERVAL / 1000);
                scheduleStatsPoll(POLL_INTERVAL);
                setPipelines(_normaliseStats(raw));
            })
            .catch(err => {
                if (err?.name === 'AbortError') return;
                setStatsFetching(false);
                const nextDelay = Math.min(statsDelayRef.current * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL);
                statsDelayRef.current = nextDelay;
                _startCountdown(setStatsCountdown, statsCountdownRef, Math.round(nextDelay / 1000));
                scheduleStatsPoll(nextDelay);
            });
    }, [deviceName]);

    // PS info polling — runs when dialog is open
    useEffect(() => {
        if (!isOpen) return;

        const onVisibilityChange = () => {
            if (document.hidden) {
                hiddenSinceRef.current = Date.now();
            } else {
                if (hiddenSinceRef.current && Date.now() - hiddenSinceRef.current > 5 * 60 * 1000)
                    fetchPsInfo();
                hiddenSinceRef.current = null;
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        psDelayRef.current = POLL_INTERVAL;
        fetchPsInfo();

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);

            if (psTimeoutRef.current) clearTimeout(psTimeoutRef.current);
            if (psCountdownRef.current) clearInterval(psCountdownRef.current);
        };
    }, [isOpen]);

    // Stats polling — only runs when pipelines section is expanded
    useEffect(() => {
        if (!isOpen || !pipelinesExpanded) return;

        statsDelayRef.current = POLL_INTERVAL;
        fetchStats();

        return () => {

            if (statsTimeoutRef.current) clearTimeout(statsTimeoutRef.current);
            if (statsCountdownRef.current) clearInterval(statsCountdownRef.current);
        };
    }, [isOpen, pipelinesExpanded]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setPipelinesExpanded(false);
        setHistory({cpu: [], ram: [], swap: [], disk: []});
        setLatest(null);
        setPipelines([]);
        setUnresponsive(false);
        setError(null);
        setPsFetching(false);
        setStatsFetching(false);
        setPsCountdown(POLL_INTERVAL / 1000);
        setStatsCountdown(POLL_INTERVAL / 1000);
        psDelayRef.current = POLL_INTERVAL;
        statsDelayRef.current = POLL_INTERVAL;
    }, []);

    const cpuPct = latest?.cpu?.cpu_percent;
    const ramPct = latest?.ram?.percent;
    const swapPct = latest?.swap?.percent;
    const diskPct = latest?.disk?.percent;

    return (
        <>
            <Button small minimal icon="dashboard" onClick={() => setIsOpen(true)} />
            <Dialog
                isOpen={isOpen}
                onClose={handleClose}
                title={<span style={{display: 'flex', alignItems: 'center', gap: '0.5em'}}>
                    {deviceName}
                    {unresponsive && <Tag intent="danger" icon="warning-sign" minimal style={{fontSize: 11}}>Not responding</Tag>}
                </span>}
                icon="dashboard"
                style={{width: 660, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}
            >
                <DialogBody>
                    <DialogContent>
                        <SectionDivider>
                            System
                            <CountdownTimer loading={psFetching} seconds={psCountdown} />
                        </SectionDivider>

                        {!latest && <div style={{textAlign: 'center'}}>
                            {psFetching
                                ? <Spinner size={24} />
                                : unresponsive && <>
                                    <Icon icon="warning-sign" size={24} />
                                    <div style={{fontSize: 12, color: '#8a9ba8', marginTop: '0.3em'}}>Retrying in {psCountdown}s...</div>
                                </>
                            }
                        </div>}

                        {latest && <>
                            <SystemSection>
                                <MetricCard>
                                    <MetricHeader>
                                        <MetricTitle>CPU</MetricTitle>
                                        <Tag intent={_intentForPercent(cpuPct)} minimal round style={{fontSize: 12, minHeight: 18, padding: '0 6px'}}>
                                            {cpuPct != null ? `${cpuPct.toFixed(1)}%` : '?'}
                                        </Tag>
                                    </MetricHeader>
                                    <Sparkline data={history.cpu} color="#2965cc" />
                                    <MetricDetails><span>{latest.cpu?.cpu_count ?? '?'} cores</span></MetricDetails>
                                </MetricCard>

                                <MetricCard>
                                    <MetricHeader>
                                        <MetricTitle>RAM</MetricTitle>
                                        <Tag intent={_intentForPercent(ramPct)} minimal round style={{fontSize: 12, minHeight: 18, padding: '0 6px'}}>
                                            {ramPct != null ? `${ramPct.toFixed(1)}%` : '?'}
                                        </Tag>
                                    </MetricHeader>
                                    <Sparkline data={history.ram} color="#29a634" />
                                    <MetricDetails>
                                        <span>{_formatBytes(latest.ram?.used)} / {_formatBytes(latest.ram?.total)}</span>
                                    </MetricDetails>
                                </MetricCard>

                                <MetricCard>
                                    <MetricHeader>
                                        <MetricTitle>Swap</MetricTitle>
                                        <Tag intent={_intentForPercent(swapPct)} minimal round style={{fontSize: 12, minHeight: 18, padding: '0 6px'}}>
                                            {swapPct != null ? `${swapPct.toFixed(1)}%` : '?'}
                                        </Tag>
                                    </MetricHeader>
                                    <Sparkline data={history.swap} color="#d99e0b" />
                                    <MetricDetails>
                                        <span>{_formatBytes(latest.swap?.used)} / {_formatBytes(latest.swap?.total)}</span>
                                    </MetricDetails>
                                </MetricCard>

                                <MetricCard>
                                    <MetricHeader>
                                        <MetricTitle>Disk</MetricTitle>
                                        <Tag intent={_intentForPercent(diskPct)} minimal round style={{fontSize: 12, minHeight: 18, padding: '0 6px'}}>
                                            {diskPct != null ? `${diskPct.toFixed(1)}%` : '?'}
                                        </Tag>
                                    </MetricHeader>
                                    <Sparkline data={history.disk} color="#c23030" />
                                    <MetricDetails>
                                        <span>{_formatBytes(latest.disk?.used)} / {_formatBytes(latest.disk?.total)}</span>
                                    </MetricDetails>
                                </MetricCard>
                            </SystemSection>
                        </>}

                        <SectionDivider
                            style={{cursor: 'pointer'}}
                            onClick={() => setPipelinesExpanded(prev => !prev)}
                        >
                            <Icon icon={pipelinesExpanded ? 'chevron-down' : 'chevron-right'} size={14} style={{order: 0}} />
                            Pipelines
                            <CountdownTimer loading={pipelinesExpanded && statsFetching} seconds={pipelinesExpanded ? statsCountdown : null} />
                        </SectionDivider>
                        {pipelinesExpanded && <>
                            {!pipelines.length && <div style={{textAlign: 'center'}}>
                                {statsFetching
                                    ? <Spinner size={20} />
                                    : <>
                                        <Icon icon="warning-sign" size={20} />
                                        <div style={{fontSize: 12, color: '#8a9ba8', marginTop: '0.3em'}}>Retrying in {statsCountdown}s...</div>
                                    </>
                                }
                            </div>}
                            {pipelines.length > 0 && <PipelineStats pipelines={pipelines} />}
                        </>}
                    </DialogContent>
                </DialogBody>
            </Dialog>
        </>
    );
};

export {DeviceTopOverlay};